// src/pages/Question/Question.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import { toast } from 'react-hot-toast';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  arrayUnion,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import Sidebar from './Sidebar';
import QuestionPane from './QuestionPane';
import Modal from '@/components/Modal';

function Question() {
  const { globalUser, globalData, defaultAvatarUrl } = useAuth();

  // State
  const [askQuestion, setAskQuestion] = useState(false);
  const [askQuestionStep, setAskQuestionStep] = useState(0);
  const [openQuestions, setOpenQuestions] = useState([]);
  const [closedQuestions, setClosedQuestions] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDog, setSelectedDog] = useState('');
  const [dogs, setDogs] = useState([]);

  const [messageInput, setMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Modal state centralised
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState('');

  // 🔄 Firestore subscriptions
  useEffect(() => {
    const unsubProfiles = onSnapshot(collection(db, 'users'), (snap) => {
      const profiles = {};
      snap.forEach((doc) => (profiles[doc.id] = doc.data()));
      setUserProfiles(profiles);
    });
    return unsubProfiles;
  }, []);

  useEffect(() => {
    if (!globalUser?.uid) return;
    const q = query(collection(db, `users/${globalUser.uid}/dogs`));
    return onSnapshot(q, (snap) => {
      setDogs(
        snap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().dogName || 'Unknown',
        }))
      );
    });
  }, [globalUser]);

  useEffect(() => {
    const ref = collection(db, 'questions');
    const q = globalData.isAdmin
      ? query(ref)
      : query(ref, where('userId', '==', globalUser.uid));

    return onSnapshot(q, (snap) => {
      const qs = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setOpenQuestions(qs.filter((q) => q.status === 'open'));
      setClosedQuestions(qs.filter((q) => q.status === 'closed'));
    });
  }, [globalUser, globalData.isAdmin]);

  // Fetch Site Admins
  useEffect(() => {
    const q = query(collection(db, 'users'), where('isAdmin', '==', true));
    return onSnapshot(q, (snap) => {
      setAvailableAdmins(
        snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    });
  }, []);

  // Handlers (delete, assign, save, reply, reopen) centralised here
  const handleDeleteQuestion = useCallback(
    async (questionId) => {
      try {
        await deleteDoc(doc(db, 'questions', questionId));
        setOpenQuestions((prev) => prev.filter((q) => q.id !== questionId));
        setClosedQuestions((prev) => prev.filter((q) => q.id !== questionId));
        if (currentQuestion?.id === questionId) setCurrentQuestion(null);
        toast.success('Question deleted.');
      } catch (err) {
        toast.error('Failed to delete question.');
      }
    },
    [currentQuestion]
  );

  const handleAssignAdmin = async (questionId) => {
    try {
      await updateDoc(doc(db, 'questions', questionId), {
        adminId: globalUser.uid,
      });
      toast.success('Assigned to you.');
    } catch {
      toast.error('Failed to assign admin.');
    }
  };

  const handleReopenQuestion = async (questionId) => {
    try {
      await updateDoc(doc(db, 'questions', questionId), { status: 'open' });
      toast.success('Reopened.');
    } catch {
      toast.error('Failed to reopen question.');
    }
  };

  const handleReplyToQuestion = async (questionId, replyText) => {
    if (!replyText.trim()) return;
    try {
      const replyMessage = {
        senderId: globalUser.uid,
        sender: globalUser.isAdmin ? 'Admin' : 'User',
        text: replyText,
        timestamp: new Date(),
      };
      await updateDoc(doc(db, 'questions', questionId), {
        messages: arrayUnion(replyMessage),
      });
      setMessageInput('');
    } catch {
      toast.error('Reply failed.');
    }
  };

  const handleSaveQuestion = async () => {
    if (!messageInput.trim()) return;
    try {
      const newMessage = {
        senderId: globalUser.uid,
        sender: globalUser.isAdmin ? 'Admin' : 'User',
        text: messageInput,
        timestamp: new Date(),
      };
      const newQ = {
        userId: globalUser.uid,
        adminId: null,
        category: selectedCategory,
        selectedDogs:
          selectedDog === 'All Above' ? dogs.map((d) => d.name) : [selectedDog],
        messages: [newMessage],
        status: 'open',
        createdAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'questions'), newQ);
      setCurrentQuestion({ id: ref.id, ...newQ });
      setAskQuestion(false);
      setMessageInput('');
    } catch {
      toast.error('Save failed.');
    }
  };

  // UI helpers
  const startAskFlow = () => {
    setAskQuestion(true);
    setAskQuestionStep(1);
    setSelectedCategory('');
    setSelectedDog('');
    setCurrentQuestion(null);
    setMessageInput('');
  };

  const selectQuestion = (q) => {
    setAskQuestion(false);
    setAskQuestionStep(0);
    setCurrentQuestion(q);
  };

  return (
    <Layout>
      <div className='row'>
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body p-0'>
              <div className='row g-0'>
                <div className='col-xl-4 border-end'>
                  <Sidebar
                    openQuestions={openQuestions}
                    closedQuestions={closedQuestions}
                    groupedAdmins={availableAdmins}
                    handleDeleteQuestion={handleDeleteQuestion}
                    handleAssignAdmin={handleAssignAdmin}
                    globalUser={globalUser}
                    globalData={globalData}
                    userProfiles={userProfiles}
                    defaultAvatarUrl={defaultAvatarUrl}
                    onStartAsk={startAskFlow}
                    onSelectQuestion={selectQuestion}
                    setSelectedQuestion={setSelectedQuestion}
                    setSelectedQuestionId={setSelectedQuestionId}
                  />
                </div>
                <div className='col-xl-8'>
                  <div className='chat-box-area p-3'>
                    <QuestionPane
                      currentQuestion={currentQuestion}
                      setCurrentQuestion={setCurrentQuestion}
                      userProfiles={userProfiles}
                      globalData={globalData}
                      defaultAvatarUrl={defaultAvatarUrl}
                      messageInput={messageInput}
                      setMessageInput={setMessageInput}
                      handleReplyToQuestion={handleReplyToQuestion}
                      handleAssignAdmin={handleAssignAdmin}
                      handleReopenQuestion={handleReopenQuestion}
                      globalUser={globalUser}
                      askQuestion={askQuestion}
                      askQuestionStep={askQuestionStep}
                      setAskQuestionStep={setAskQuestionStep}
                      dogs={dogs}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      selectedDog={selectedDog}
                      setSelectedDog={setSelectedDog}
                      handleSaveQuestion={handleSaveQuestion}
                      isLoading={isLoading}
                      setSelectedQuestion={setSelectedQuestion}
                      setSelectedQuestionId={setSelectedQuestionId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        id='confirmAssignAdmin'
        title={`Do you want to Assign Yourself?`}
        onConfirm={() => handleAssignAdmin(selectedQuestionId)}
        confirmText='Yes'
        size='medium'
      >
        <p>Are you sure you want to assign yourself to this Question?</p>
      </Modal>

      <Modal
        id='confirmDeleteQuestion'
        title={`Remove ${selectedQuestion}?`}
        onConfirm={() => handleAssignAdmin(selectedQuestionId)}
        confirmText='Yes'
        size='medium'
      >
        <p>Are you sure you want to delete {selectedQuestion}?</p>
      </Modal>

      <Modal
        id='confirmCloseQuestion'
        title={`Close This Question?`}
        onConfirm={() => handleDeleteQuestion(selectedQuestionId)}
        confirmText='Yes'
        size='medium'
      >
        <p>
          Are you sure you want to mark the question as closed{' '}
          {selectedQuestion}?
        </p>
      </Modal>

      <Modal
        id='confirmReOpenQuestion'
        title={`Reopen This Question?`}
        onConfirm={() => handleDeleteQuestion(selectedQuestionId)}
        confirmText='Yes'
        size='medium'
      >
        <p>Are you sure you want to re-open the question?</p>
      </Modal>
    </Layout>
  );
}

export default Question;
